import { message } from 'antd';
import { QueryClient } from 'react-query';
import { put, select, takeLatest } from 'redux-saga/effects';
import { Role } from 'api/models';
import {
  PlanningLevelConfig,
  ResourceManagementConfig,
} from 'api/resource-management/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import { RootState } from 'data';
import { StoreActionProps } from 'data/core';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { TemplatesAPI } from 'data/templates/api';
import { getResourceManagementConfigQueryKey } from 'queries/resource-management-configuration';
import {
  ROLES_QUERY_KEY,
  ROLES_WITH_PLACEHOLDER_QUERY_KEY,
} from 'queries/roles';
import { logger } from 'services';
import { TeamsStore } from 'stores/teams';
import {
  fetchProjectTemplateStart,
  fetchProjectTemplateStartSuccess,
} from '..';
import { fetchProjectTemplate } from '../actions';
import { getDynamicConditionsFromPayload } from '../api/transform';
import { getProjectTemplatePhaseIds } from '../selectors';
import {
  setDynamicConditions,
  setProjectTemplateActiveRecord,
} from '../slices';
import { ProjectTemplateApi } from '../types';
import { generateNewTaskRows, hoursPerWeekModifierRedux } from './utils';

function* fetchProjectTemplateSaga(
  action: StoreActionProps<{
    templateId: number;
    dynamicConditionFields: FilterableField[];
    data: ProjectTemplateApi;
  }>,
) {
  const queryClient = (yield getQueryClientContext()) as QueryClient;

  try {
    const { payload } = action;
    const { templateId, dynamicConditionFields } = payload;

    yield put(fetchProjectTemplateStart({ templateId }));

    const data: ProjectTemplateApi =
      yield TemplatesAPI.getById<ProjectTemplateApi>(templateId);

    const rmConfig = queryClient.getQueryData<ResourceManagementConfig>(
      getResourceManagementConfigQueryKey(),
    );

    const newData = hoursPerWeekModifierRedux(
      data,
      rmConfig?.planningLevelConfig === PlanningLevelConfig.HoursPerWeek,
    );

    const dataWithNewTaskRows = generateNewTaskRows(newData);

    const rolesWithPlaceholder = queryClient.getQueryData<Role[]>(
      ROLES_WITH_PLACEHOLDER_QUERY_KEY,
    );
    const roles = queryClient.getQueryData<Role[]>(ROLES_QUERY_KEY);
    const { byId: teamsById }: TeamsStore = yield select(
      ({ teams }: RootState) => teams,
    );

    yield put(
      fetchProjectTemplateStartSuccess({
        template: dataWithNewTaskRows,
        rolesWithPlaceholder,
        allRoles: roles,
        teamsLookup: teamsById,
      }),
    );

    const phaseIds = yield select(getProjectTemplatePhaseIds(templateId));

    if (phaseIds[0]) {
      yield put(
        setProjectTemplateActiveRecord({
          id: phaseIds[0],
          type: 'PROJECT_PHASE',
        }),
      );
    }

    const dynamicConditions = getDynamicConditionsFromPayload(
      data,
      dynamicConditionFields,
    );

    yield put(setDynamicConditions(dynamicConditions));
  } catch (error) {
    logger.error('an error fetching the project template', { error });

    message.error('There was an error fetching the project template');
  }
}

export function* watchFetchProjectTemplateSaga(): Generator {
  yield takeLatest(fetchProjectTemplate.type, fetchProjectTemplateSaga);
}
